import { useEffect, useState } from 'react';
import { attorneyApi, type AttorneyFirm, type Attorney } from '../api';
import Select from '../components/common/Select';
import Icon from '../components/common/Icon';

// Tenant-scoped book of instructing attorneys and their firms. Referenced by
// matters (primary_firm_id / instructing_attorney_id) so counsel can brief
// and invoice them.

export default function AttorneysPage() {
  const [tab, setTab] = useState<'attorneys' | 'firms'>('attorneys');
  const [firms, setFirms] = useState<AttorneyFirm[]>([]);
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [q, setQ] = useState('');
  const [firmFilter, setFirmFilter] = useState<number | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [attyModal, setAttyModal] = useState<Attorney | 'new' | null>(null);
  const [firmModal, setFirmModal] = useState<AttorneyFirm | 'new' | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [fr, ar] = await Promise.all([
        attorneyApi.listFirms(q || undefined),
        attorneyApi.list({ q: q || undefined, firm_id: firmFilter === 'all' ? undefined : firmFilter }),
      ]);
      setFirms(fr.firms);
      setAttorneys(ar.attorneys);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [firmFilter]);

  const firmById = new Map(firms.map((f) => [f.ROWID, f]));

  const removeAtty = async (a: Attorney) => {
    if (!confirm(`Delete attorney ${a.last_name}?`)) return;
    try { await attorneyApi.remove(a.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };
  const removeFirm = async (f: AttorneyFirm) => {
    if (!confirm(`Delete firm "${f.name}"?`)) return;
    try { await attorneyApi.deleteFirm(f.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };

  return (
    <div className="stack">
      <div className="row">
        <h1 className="grow">Attorneys</h1>
        <button onClick={() => setFirmModal('new')}>+ Firm</button>
        <button className="primary" onClick={() => setAttyModal('new')}>+ Attorney</button>
      </div>

      <div className="tabs" style={{ width: 'fit-content' }}>
        <div className={`tab ${tab === 'attorneys' ? 'active' : ''}`} onClick={() => setTab('attorneys')}>
          Attorneys <span className="muted" style={{ marginLeft: 6 }}>({attorneys.length})</span>
        </div>
        <div className={`tab ${tab === 'firms' ? 'active' : ''}`} onClick={() => setTab('firms')}>
          Firms <span className="muted" style={{ marginLeft: 6 }}>({firms.length})</span>
        </div>
      </div>

      <div className="row">
        <input
          className="grow"
          placeholder={tab === 'attorneys' ? 'Search attorneys by name or email…' : 'Search firms by name…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        {tab === 'attorneys' && (
          <div style={{ width: 240 }}>
            <Select
              value={firmFilter === 'all' ? 'all' : String(firmFilter)}
              onChange={(v) => setFirmFilter(v === 'all' ? 'all' : Number(v))}
              options={[
                { value: 'all', label: 'All firms' },
                ...firms.map((f) => ({ value: String(f.ROWID), label: f.name })),
              ]}
              searchPlaceholder="Filter by firm…"
            />
          </div>
        )}
        <button onClick={load}>Refresh</button>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading…</div>
        ) : tab === 'attorneys' ? (
          attorneys.length === 0 ? (
            <p className="muted" style={{ padding: 14 }}>No attorneys yet. Add one to brief them on a matter.</p>
          ) : (
            <table>
              <thead>
                <tr><th>Name</th><th>Firm</th><th>Email</th><th>Phone</th><th style={{ width: 160 }}></th></tr>
              </thead>
              <tbody>
                {attorneys.map((a) => (
                  <tr key={a.ROWID}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {[a.title, a.first_name, a.last_name].filter(Boolean).join(' ')}
                      </div>
                    </td>
                    <td>{a.firm_id ? firmById.get(a.firm_id)?.name || `#${a.firm_id}` : <span className="muted">—</span>}</td>
                    <td>{a.email || <span className="muted">—</span>}</td>
                    <td>{a.phone || <span className="muted">—</span>}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button onClick={() => setAttyModal(a)}>Edit</button>
                      <button onClick={() => removeAtty(a)} style={{ marginLeft: 6 }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          firms.length === 0 ? (
            <p className="muted" style={{ padding: 14 }}>No firms yet.</p>
          ) : (
            <table>
              <thead>
                <tr><th>Firm</th><th>VAT no.</th><th>Email</th><th>Phone</th><th style={{ width: 160 }}></th></tr>
              </thead>
              <tbody>
                {firms.map((f) => (
                  <tr key={f.ROWID}>
                    <td style={{ fontWeight: 500 }}>
                      <div className="row" style={{ gap: 8 }}>
                        <Icon name="balance-scale" size={14} style={{ color: 'var(--color-text-muted)' }} />
                        <span>{f.name}</span>
                      </div>
                    </td>
                    <td>{f.vat_number || <span className="muted">—</span>}</td>
                    <td>{f.email || <span className="muted">—</span>}</td>
                    <td>{f.phone || <span className="muted">—</span>}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button onClick={() => setFirmModal(f)}>Edit</button>
                      <button onClick={() => removeFirm(f)} style={{ marginLeft: 6 }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {attyModal && (
        <AttorneyModal
          attorney={attyModal === 'new' ? null : attyModal}
          firms={firms}
          onClose={() => setAttyModal(null)}
          onSaved={() => { setAttyModal(null); load(); }}
        />
      )}
      {firmModal && (
        <FirmModal
          firm={firmModal === 'new' ? null : firmModal}
          onClose={() => setFirmModal(null)}
          onSaved={() => { setFirmModal(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------- Attorney modal ----------------

function AttorneyModal({
  attorney, firms, onClose, onSaved,
}: { attorney: Attorney | null; firms: AttorneyFirm[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    title: attorney?.title || '',
    first_name: attorney?.first_name || '',
    last_name: attorney?.last_name || '',
    email: attorney?.email || '',
    phone: attorney?.phone || '',
    firm_id: attorney?.firm_id || null as number | null,
    notes: attorney?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (attorney) await attorneyApi.update(attorney.ROWID, form);
      else          await attorneyApi.create(form);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={attorney ? 'Edit attorney' : 'New attorney'} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <div style={{ width: 120 }}>
            <label>Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Mr/Ms/Adv" />
          </div>
          <div className="grow">
            <label>First name</label>
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="grow">
            <label>Last name</label>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
          </div>
        </div>
        <div>
          <label>Firm</label>
          <Select
            value={form.firm_id ? String(form.firm_id) : ''}
            onChange={(v) => setForm({ ...form, firm_id: v ? Number(v) : null })}
            options={[{ value: '', label: '— independent —' }, ...firms.map((f) => ({ value: String(f.ROWID), label: f.name }))]}
            searchPlaceholder="Search firms…"
          />
        </div>
        <div className="row">
          <div className="grow">
            <label>Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grow">
            <label>Phone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div>
          <label>Notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving || !form.last_name}>
            {saving ? 'Saving…' : (attorney ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Firm modal ----------------

function FirmModal({
  firm, onClose, onSaved,
}: { firm: AttorneyFirm | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: firm?.name || '',
    vat_number: firm?.vat_number || '',
    email: firm?.email || '',
    phone: firm?.phone || '',
    notes: firm?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (firm) await attorneyApi.updateFirm(firm.ROWID, form);
      else      await attorneyApi.createFirm(form);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={firm ? 'Edit firm' : 'New firm'} onClose={onClose}>
      <div className="stack">
        <div>
          <label>Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label>VAT number</label>
          <input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
        </div>
        <div className="row">
          <div className="grow">
            <label>Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grow">
            <label>Phone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <div>
          <label>Notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving || !form.name}>
            {saving ? 'Saving…' : (firm ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Modal shell ----------------

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div className="card" style={{ width: 560, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 10 }}>
          <h3 className="grow" style={{ margin: 0 }}>{title}</h3>
          <button className="ghost" onClick={onClose} style={{ width: 32, height: 32, padding: 0 }}>
            <Icon name="x" size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
